import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Flashlight, FlashlightOff, X, ZoomIn } from 'lucide-react'
import type { BarcodeFormat } from '@/lib/api-types'
import { Button } from '@/ui/Button'
import { confirmScan } from '@/lib/feedback'
import { cx } from '@/lib/cx'
import { useCamera } from './CameraProvider'
import { useDecoder, type ScanHit } from './useDecoder'
import { useWakeLock } from './useWakeLock'
import { useFramePump } from './useFramePump'
import { Overlay } from './Overlay'
import { familiesFor } from './formats'
import { clampZoom, MAX_ZOOM, MIN_ZOOM } from './roi'
import { loadZoom, saveZoom } from './prefs'

interface Props {
  formats: readonly BarcodeFormat[]
  workflowName: string
  onDetected: (hit: ScanHit) => void
  onClose: () => void
  /** Pauses decoding without tearing the camera down (e.g. a form is open). */
  paused?: boolean
}

/** How long the confirmation highlight stays on screen before resuming. */
const HOLD_MS = 900

/**
 * How long the zoom slider has to settle before the value is written.
 *
 * A range input fires `change` on every pixel of a drag and localStorage is
 * synchronous, so writing per event would stall the frame pump mid-scan.
 */
const ZOOM_SAVE_DEBOUNCE_MS = 300

/** Ties the disclosure button to the panel it opens, for screen readers. */
const ZOOM_PANEL_ID = 'scanner-zoom-panel'

export function ScannerView({ formats, workflowName, onDetected, onClose, paused = false }: Props) {
  const camera = useCamera()
  const videoRef = useRef<HTMLVideoElement>(null)

  // Restored from the last scan rather than reset to 1x — see `prefs.ts`.
  const [zoom, setZoom] = useState(loadZoom)
  const [showZoom, setShowZoom] = useState(false)
  const [held, setHeld] = useState<ScanHit | null>(null)
  const [soundOn, setSoundOn] = useState(true)

  const zoomSliderRef = useRef<HTMLInputElement>(null)
  const lastTextRef = useRef<string | null>(null)
  const holdTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  const family = familiesFor(formats)[0] ?? '2d'
  const active = !paused && held === null

  const handleHit = useCallback(
    (hit: ScanHit) => {
      // Guard against the same physical code firing repeatedly while it stays
      // in frame — without this a single label submits several times.
      if (lastTextRef.current === hit.text) return
      lastTextRef.current = hit.text

      setHeld(hit)
      confirmScan(soundOn)

      holdTimerRef.current = setTimeout(() => {
        setHeld(null)
        lastTextRef.current = null
      }, HOLD_MS)

      onDetected(hit)
    },
    [onDetected, soundOn],
  )

  const decoder = useDecoder({ formats, zoom, onHit: handleHit, enabled: active })
  const { releaseBuffers } = decoder

  useFramePump(videoRef, decoder.pump, active && camera.status === 'live')
  useWakeLock(camera.status === 'live')

  // Depend on `camera.start` (a stable useCallback), not the whole context
  // object — that changes identity whenever status or the device list updates,
  // which would re-run this on every camera state transition.
  const startCamera = camera.start
  useEffect(() => {
    void startCamera()
  }, [startCamera])

  // Attach the live stream to the element. Detaching on unmount stops the
  // preview without touching the track, which is what keeps the session alive.
  useEffect(() => {
    const video = videoRef.current
    const stream = camera.stream
    if (video === null || stream === null) return

    if (video.srcObject !== stream) video.srcObject = stream
    void video.play().catch(() => undefined)

    return () => {
      video.pause()
      video.srcObject = null
    }
  }, [camera.stream])

  // Depends on the stable `releaseBuffers` callback, NOT the decoder object.
  // `useDecoder` returns a fresh object literal every render, so depending on
  // it would run this cleanup after every single render — throwing away the
  // scratch canvas continuously and forcing a reallocation per frame, which is
  // exactly the memory churn this scanner is designed to avoid.
  useEffect(
    () => () => {
      if (holdTimerRef.current !== null) clearTimeout(holdTimerRef.current)
      releaseBuffers()
    },
    [releaseBuffers],
  )

  useEffect(() => {
    const timer = setTimeout(() => saveZoom(zoom), ZOOM_SAVE_DEBOUNCE_MS)
    return () => clearTimeout(timer)
  }, [zoom])

  // Opening the panel from the keyboard should land on the control it opened.
  useEffect(() => {
    if (showZoom) zoomSliderRef.current?.focus()
  }, [showZoom])

  const coaching = useMemo(() => {
    if (camera.status !== 'live') return null
    if (held !== null) return null
    if (!decoder.ready) return 'Getting the scanner ready…'
    if (decoder.stats.misses > 45) return 'Still nothing — move closer, or tap Zoom'
    if (decoder.stats.misses > 18) return 'Hold steady and fill the box with the code'
    return null
  }, [camera.status, decoder.ready, decoder.stats.misses, held])

  return (
    <div className="fixed inset-0 z-[80] flex flex-col bg-black">
      <video
        ref={videoRef}
        className="absolute inset-0 h-full w-full object-cover"
        playsInline
        muted
        // autoPlay is deliberately absent: iOS is unreliable about honouring it
        // for a srcObject set after mount, so play() is called explicitly above.
        aria-label={`Camera preview for ${workflowName}`}
      />

      <Overlay
        videoRef={videoRef}
        family={family}
        zoom={zoom}
        corners={held?.corners ?? null}
      />

      {/* Header */}
      <div className="pt-safe relative z-10 flex items-start justify-between gap-3 p-4">
        <div className="min-w-0 rounded-2xl bg-black/45 px-3 py-2 backdrop-blur-sm">
          <p className="truncate text-[0.9375rem] font-semibold text-white">{workflowName}</p>
          <p className="text-xs text-white/70">
            {camera.status === 'live' && decoder.ready ? 'Ready to scan' : 'Starting camera…'}
          </p>
        </div>
        <button
          type="button"
          onClick={onClose}
          aria-label="Close scanner"
          className="grid h-touch w-touch shrink-0 place-items-center rounded-full bg-black/45 text-white backdrop-blur-sm"
        >
          <X className="h-6 w-6" aria-hidden="true" />
        </button>
      </div>

      <div className="flex-1" />

      {/* Status / errors */}
      {camera.status === 'denied' && (
        <div className="relative z-10 mx-4 mb-4 rounded-[var(--radius-card)] bg-white p-5 text-center">
          <h2 className="text-lg font-bold">Camera access is blocked</h2>
          <p className="mt-2 text-[0.9375rem] text-[var(--text-secondary)]">
            Open Settings → Safari → Camera and set it to Allow, then reopen ScanFlow.
          </p>
          <Button className="mt-4" fullWidth onClick={() => void camera.start()}>
            Try again
          </Button>
        </div>
      )}

      {(camera.status === 'error' || decoder.error !== null) && (
        <div className="relative z-10 mx-4 mb-4 rounded-[var(--radius-card)] bg-white p-5 text-center">
          <h2 className="text-lg font-bold">The scanner couldn&rsquo;t start</h2>
          <p className="mt-2 text-[0.9375rem] text-[var(--text-secondary)]">
            {camera.error ?? decoder.error}
          </p>
          <Button className="mt-4" fullWidth onClick={() => void camera.start()}>
            Try again
          </Button>
        </div>
      )}

      {coaching !== null && (
        <p
          role="status"
          className="relative z-10 mx-auto mb-4 max-w-xs rounded-full bg-black/55 px-4 py-2 text-center text-sm text-white backdrop-blur-sm"
        >
          {coaching}
        </p>
      )}

      {/* Controls sit in the bottom third: this is a one-handed, thumb-reach
          surface, and the old app put its controls along the top edge. */}
      <div className="pb-safe relative z-10 space-y-3 p-4">
        {/* Hidden until asked for. The level survives between scans, so the
            slider was costing a row of the viewfinder to show a number that
            rarely changes — the chip below carries that number instead. */}
        {showZoom && (
          <div
            id={ZOOM_PANEL_ID}
            className="flex items-center gap-3 rounded-[var(--radius-card)] bg-black/45 px-4 py-3 backdrop-blur-sm"
          >
            <ZoomIn className="h-5 w-5 shrink-0 text-white/70" aria-hidden="true" />
            <input
              ref={zoomSliderRef}
              type="range"
              min={MIN_ZOOM}
              max={MAX_ZOOM}
              step={0.1}
              value={zoom}
              onChange={(e) => setZoom(clampZoom(Number(e.target.value)))}
              aria-label="Zoom"
              className="h-touch w-full accent-brand-500"
            />
            <span className="w-10 shrink-0 text-right text-sm font-semibold tabular-nums text-white">
              {zoom.toFixed(1)}×
            </span>
            <button
              type="button"
              onClick={() => setZoom(MIN_ZOOM)}
              disabled={zoom === MIN_ZOOM}
              className="min-h-touch shrink-0 text-sm font-semibold text-white disabled:text-white/40"
            >
              Reset
            </button>
          </div>
        )}

        {/* Highlighted white means on, for every chip in this row. */}
        <div className="flex flex-wrap items-center justify-center gap-3">
          <button
            type="button"
            onClick={() => setShowZoom((v) => !v)}
            // The white highlight says "engaged" to anyone who can see it; this
            // is the same fact for anyone who can't. Kept prefixed with the
            // visible label so the accessible name still contains it.
            aria-label={zoom > MIN_ZOOM ? `Zoom, currently ${zoom.toFixed(1)} times` : 'Zoom'}
            aria-expanded={showZoom}
            aria-controls={showZoom ? ZOOM_PANEL_ID : undefined}
            className={cx(
              'flex min-h-touch items-center gap-2 rounded-full px-4 text-sm font-semibold backdrop-blur-sm',
              // Highlighted whenever the crop is tighter than the full frame.
              // A remembered zoom that left no trace on screen would just look
              // like a camera that had stopped seeing the edges of the label.
              zoom > MIN_ZOOM ? 'bg-white text-slate-900' : 'bg-black/45 text-white',
            )}
          >
            <ZoomIn className="h-5 w-5" aria-hidden="true" />
            Zoom
          </button>

          {/* Rendered only where the platform actually supports it. iOS exposes
              no torch capability at all, so on iPhone this button never appears
              rather than appearing and doing nothing. */}
          {camera.torchSupported && (
            <button
              type="button"
              onClick={() => void camera.setTorch(!camera.torchOn)}
              aria-pressed={camera.torchOn}
              className={cx(
                'flex min-h-touch items-center gap-2 rounded-full px-4 text-sm font-semibold backdrop-blur-sm',
                camera.torchOn ? 'bg-white text-slate-900' : 'bg-black/45 text-white',
              )}
            >
              {camera.torchOn ? (
                <Flashlight className="h-5 w-5" aria-hidden="true" />
              ) : (
                <FlashlightOff className="h-5 w-5" aria-hidden="true" />
              )}
              Light
            </button>
          )}

          <button
            type="button"
            onClick={() => setSoundOn((v) => !v)}
            aria-pressed={soundOn}
            className={cx(
              'min-h-touch rounded-full px-4 text-sm font-semibold backdrop-blur-sm',
              soundOn ? 'bg-white text-slate-900' : 'bg-black/45 text-white',
            )}
          >
            Beep
          </button>
        </div>
      </div>
    </div>
  )
}
